import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  Modal,
  Pressable,
  View,
  type ViewStyle,
} from 'react-native';
import { Image } from 'expo-image';
import Animated, { FadeIn, FadeOut, useReducedMotion } from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { convertImageBlobToJpeg } from '@/utils/jpeg-convert';
import { createLogger } from '@/utils/logger';
import type { ImageViewerProps, ViewerPhoto } from './image-viewer.types';
import { ImageViewerCaption } from './image-viewer-caption';
import { useCheckInCaptionPress } from './use-check-in-caption-press';
import {
  buildPhotoAccessibilityLabel,
  downloadFileName,
  neighborIndices,
  prefetchViewerPhotos,
  viewerImageSources,
} from './image-viewer.utils';


// Web chrome geometry. The caption panel is lifted by ZOOM_BAR_HEIGHT so the
// zoom bar (drawn above it) never covers the caption text.
const CHROME_BUTTON_SIZE = 48;
const ZOOM_BAR_PADDING_TOP = 8;
const ZOOM_BAR_PADDING_BOTTOM = 20;
const ZOOM_BAR_HEIGHT = ZOOM_BAR_PADDING_TOP + CHROME_BUTTON_SIZE + ZOOM_BAR_PADDING_BOTTOM;
const log = createLogger('IMAGE_VIEWER');

/**
 * Warm brand chrome for the web lightbox. These tokens mirror the native build
 * exactly so the two viewers read as one component. The old web viewer leaned on
 * pure-black / pure-white; the design system is all-warm, so every value here is
 * a warm near-black or warm off-white instead.
 */
const CHROME = {
  /** Warm near-black backdrop (not pure #000). */
  backdrop: 'rgba(8,7,6,0.96)',
  /** Translucent warm chrome-button fill. */
  button: 'rgba(30,28,25,0.72)',
  /** Hover / active chrome-button fill. */
  buttonActive: 'rgba(46,44,40,0.9)',
  /** Warm off-white for icons and counter text (not pure #fff). */
  ink: '#F2F0EB',
  /** Brand accent used for focus/active affordances. */
  accent: '#9ACD32',
} as const;

const HIT_SLOP = { top: 6, bottom: 6, left: 6, right: 6 } as const;

// Zoom bounds and steps. Web zoom is discrete (buttons / wheel / dbl-click),
// not a continuous mobile pinch, so a modest step feels responsive.
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.5;
const DOUBLE_TAP_SCALE = 2.5;
const WHEEL_ZOOM_SENSITIVITY = 0.0015;

// JPEG quality for downloaded copies; matches the native viewer's re-encode.
const DOWNLOAD_JPEG_QUALITY = 0.95;
// How long a blob: download URL stays alive after its link is clicked.
const REVOKE_DELAY_MS = 1000;

interface Point {
  x: number;
  y: number;
}

interface ZoomState {
  scale: number;
  translateX: number;
  translateY: number;
}

const IDENTITY: ZoomState = { scale: 1, translateX: 0, translateY: 0 };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clampScale(scale: number): number {
  return clamp(scale, MIN_SCALE, MAX_SCALE);
}

/**
 * Clamps a pan translation so the scaled image can never expose the backdrop
 * beyond its edges. With a center transform-origin the image can travel at most
 * half its overflow in each axis: `viewport * (scale - 1) / 2`.
 */
export function clampTranslation(
  translateX: number,
  translateY: number,
  scale: number,
  viewport: { width: number; height: number }
): { translateX: number; translateY: number } {
  const maxX = Math.max(0, (viewport.width * (scale - 1)) / 2);
  const maxY = Math.max(0, (viewport.height * (scale - 1)) / 2);
  return {
    translateX: clamp(translateX, -maxX, maxX),
    translateY: clamp(translateY, -maxY, maxY),
  };
}

/**
 * Zooms to `nextScale` about a focal `point` (in px, relative to the viewport
 * center) so the content under that point stays put. Passing the center (0,0) as
 * the focal point yields a plain centered zoom (used by the +/- buttons).
 */
export function zoomAbout(
  state: ZoomState,
  nextScaleRaw: number,
  point: Point,
  viewport: { width: number; height: number }
): ZoomState {
  const nextScale = clampScale(nextScaleRaw);
  if (nextScale === MIN_SCALE) {
    return { ...IDENTITY };
  }
  const ratio = nextScale / state.scale;
  const translateX = point.x - ratio * (point.x - state.translateX);
  const translateY = point.y - ratio * (point.y - state.translateY);
  const clamped = clampTranslation(translateX, translateY, nextScale, viewport);
  return { scale: nextScale, ...clamped };
}

interface ChromeButtonProps {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  style?: ViewStyle;
}

/** 48x48 warm translucent chrome button used for close, paging and zoom. */
function ChromeButton({ icon, label, onPress, style }: ChromeButtonProps) {
  return (
    <Pressable
      onPress={onPress}
      hitSlop={HIT_SLOP}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed }) => [
        {
          width: CHROME_BUTTON_SIZE,
          height: CHROME_BUTTON_SIZE,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 24,
          backgroundColor: pressed ? CHROME.buttonActive : CHROME.button,
        },
        style,
      ]}
    >
      <Ionicons name={icon} size={22} color={CHROME.ink} />
    </Pressable>
  );
}

/** Warm fallback shown when a photo fails to load. */
function PhotoErrorFallback() {
  return (
    <View
      testID="viewer-image-error"
      style={{ alignItems: 'center', justifyContent: 'center', gap: 12 }}
    >
      <Ionicons name="image-outline" size={48} color={CHROME.ink} />
      <Text style={{ color: CHROME.ink, fontSize: 15 }}>Couldn&apos;t load photo</Text>
    </View>
  );
}

/** Clicks a temporary `<a download>`; `newTab` for cross-origin fallbacks. */
function clickDownloadLink(href: string, fileName: string, newTab: boolean) {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  if (newTab) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Re-encode a fetched photo as JPEG for download (stored photos are WebP, which
 * many desktop tools handle poorly). Falls back to the original bytes and their
 * extension if the browser cannot convert it.
 */
async function toDownloadable(
  blob: Blob,
  original: string,
  photoId: string
): Promise<{ blob: Blob; fileName: string }> {
  try {
    const jpeg = await convertImageBlobToJpeg(blob, DOWNLOAD_JPEG_QUALITY);
    return { blob: jpeg, fileName: downloadFileName(original, photoId, 'jpg') };
  } catch (error) {
    log.warn('JPEG conversion failed, downloading the original format', error);
    return { blob, fileName: downloadFileName(original, photoId) };
  }
}

/**
 * Saves the photo as a JPEG. Browsers ignore `download` on cross-origin links,
 * so the image is fetched into a same-origin blob first (which also lets it be
 * re-encoded). Without CORS on the upload domain that fetch fails, and the
 * original opens in a new tab instead.
 */
async function savePhotoWeb(photo: ViewerPhoto): Promise<void> {
  const { original } = viewerImageSources(photo);
  let fetched: Blob;
  try {
    const response = await fetch(original);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    fetched = await response.blob();
  } catch (error) {
    log.warn('Blob download failed, opening the original instead', error);
    clickDownloadLink(original, downloadFileName(original, photo.id), true);
    return;
  }

  const { blob, fileName } = await toDownloadable(fetched, original, photo.id);
  const objectUrl = URL.createObjectURL(blob);
  clickDownloadLink(objectUrl, fileName, false);
  setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_DELAY_MS);
}

function canWebShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

async function sharePhotoWeb(photo: ViewerPhoto): Promise<void> {
  const { original } = viewerImageSources(photo);
  try {
    await navigator.share({
      title: photo.caption?.dishName?.trim() || 'Photo',
      url: original,
    });
  } catch (error) {
    // AbortError = the user closed the share sheet; nothing to report.
    if ((error as { name?: string })?.name !== 'AbortError') {
      log.warn('Share failed', error);
    }
  }
}

export function ImageViewer({
  photos,
  initialIndex,
  visible,
  onClose,
}: ImageViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState<ZoomState>(IDENTITY);
  const [imageFailed, setImageFailed] = useState(false);
  const reducedMotion = useReducedMotion();
  const captionPressFor = useCheckInCaptionPress(onClose);

  // Viewport size drives pan clamping; kept in a ref so DOM listeners read fresh
  // values without re-subscribing on every layout change.
  const viewportRef = useRef({ width: 0, height: 0 });
  const zoomRef = useRef<ZoomState>(zoom);
  zoomRef.current = zoom;
  const containerRef = useRef<View | null>(null);

  // Sync the index to initialIndex each time the viewer opens (old lines 30-34).
  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
    }
  }, [visible, initialIndex]);

  // Reset zoom/pan and error state whenever the photo changes or viewer closes.
  useEffect(() => {
    setZoom(IDENTITY);
    setImageFailed(false);
  }, [currentIndex, visible]);

  // Preload the photos either side of the current one so paging is instant.
  useEffect(() => {
    if (!visible || photos.length === 0) return;
    const index = clamp(currentIndex, 0, photos.length - 1);
    prefetchViewerPhotos(neighborIndices(index, photos.length).map((i) => photos[i]));
  }, [visible, currentIndex, photos]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);
  const goNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(photos.length - 1, prev + 1));
  }, [photos.length]);

  const zoomByStep = useCallback((delta: number, point?: Point) => {
    setZoom((prev) => {
      const focal = point ?? { x: 0, y: 0 };
      return zoomAbout(prev, prev.scale + delta, focal, viewportRef.current);
    });
  }, []);

  const resetZoom = useCallback(() => setZoom(IDENTITY), []);

  const toggleDoubleTapZoom = useCallback((point?: Point) => {
    setZoom((prev) => {
      if (prev.scale > MIN_SCALE) return { ...IDENTITY };
      const focal = point ?? { x: 0, y: 0 };
      return zoomAbout(prev, DOUBLE_TAP_SCALE, focal, viewportRef.current);
    });
  }, []);

  // --- Keyboard: Escape closes, arrows page (only when fit), +/-/0 zoom. -----
  useEffect(() => {
    if (typeof window === 'undefined' || !visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (zoomRef.current.scale <= MIN_SCALE) goPrev();
          break;
        case 'ArrowRight':
          if (zoomRef.current.scale <= MIN_SCALE) goNext();
          break;
        case '+':
        case '=':
          zoomByStep(ZOOM_STEP);
          break;
        case '-':
          zoomByStep(-ZOOM_STEP);
          break;
        case '0':
          resetZoom();
          break;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, onClose, goPrev, goNext, zoomByStep, resetZoom]);

  // --- Prevent browser page-zoom; translate pinch into IMAGE zoom. ----------
  // The user's #1 web complaint: trackpad/Safari pinch zoomed the whole page.
  // We attach NON-PASSIVE wheel + Safari gesture listeners and preventDefault so
  // the page never zooms, feeding the delta into the image transform instead.
  useEffect(() => {
    if (typeof window === 'undefined' || !visible) return;

    const focalFrom = (clientX: number, clientY: number): Point => {
      const vp = viewportRef.current;
      // Approximate: focal relative to viewport center. Exact only when the
      // container fills the window, which the fullscreen modal effectively does.
      return { x: clientX - vp.width / 2, y: clientY - vp.height / 2 };
    };

    const onWheel = (e: WheelEvent) => {
      // ctrlKey === trackpad pinch / mouse-wheel zoom on every major browser.
      if (!e.ctrlKey) return;
      e.preventDefault();
      const point = focalFrom(e.clientX, e.clientY);
      const factor = 1 - e.deltaY * WHEEL_ZOOM_SENSITIVITY;
      setZoom((prev) =>
        zoomAbout(prev, prev.scale * factor, point, viewportRef.current)
      );
    };

    // Safari-only gesture events. `scale` is cumulative since gesturestart.
    let gestureStartScale = 1;
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      gestureStartScale = zoomRef.current.scale;
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const scale = (e as unknown as { scale?: number }).scale ?? 1;
      setZoom((prev) =>
        zoomAbout(prev, gestureStartScale * scale, { x: 0, y: 0 }, viewportRef.current)
      );
    };
    const onGestureEnd = (e: Event) => e.preventDefault();

    document.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('gesturestart', onGestureStart as EventListener, {
      passive: false,
    });
    document.addEventListener('gesturechange', onGestureChange as EventListener, {
      passive: false,
    });
    document.addEventListener('gestureend', onGestureEnd as EventListener, {
      passive: false,
    });
    return () => {
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('gesturestart', onGestureStart as EventListener);
      document.removeEventListener('gesturechange', onGestureChange as EventListener);
      document.removeEventListener('gestureend', onGestureEnd as EventListener);
    };
  }, [visible]);

  // --- Double-click + click-drag pan on the image layer (DOM listeners). -----
  useEffect(() => {
    if (typeof window === 'undefined' || !visible) return;
    const node = containerRef.current as unknown as HTMLElement | null;
    if (!node || typeof node.addEventListener !== 'function') return;

    const onDblClick = (e: MouseEvent) => {
      const vp = viewportRef.current;
      toggleDoubleTapZoom({
        x: e.clientX - vp.width / 2,
        y: e.clientY - vp.height / 2,
      });
    };

    let dragging = false;
    let last: Point = { x: 0, y: 0 };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      last = { x: e.clientX, y: e.clientY };
      setZoom((prev) => {
        if (prev.scale <= MIN_SCALE) return prev;
        const clamped = clampTranslation(
          prev.translateX + dx,
          prev.translateY + dy,
          prev.scale,
          viewportRef.current
        );
        return { ...prev, ...clamped };
      });
    };
    const onMouseUp = () => {
      dragging = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (zoomRef.current.scale <= MIN_SCALE) return;
      dragging = true;
      last = { x: e.clientX, y: e.clientY };
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    node.addEventListener('dblclick', onDblClick);
    node.addEventListener('mousedown', onMouseDown);
    return () => {
      node.removeEventListener('dblclick', onDblClick);
      node.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [visible, toggleDoubleTapZoom]);

  if (!visible || photos.length === 0) return null;

  const safeIndex = clamp(currentIndex, 0, photos.length - 1);
  const photo = photos[safeIndex];
  const isZoomed = zoom.scale > MIN_SCALE;
  const canGoPrev = safeIndex > 0;
  const canGoNext = safeIndex < photos.length - 1;
  const sources = viewerImageSources(photo);
  const showShare = canWebShare();

  const onContainerLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    viewportRef.current = { width, height };
  };

  const zoomLayerStyle: ViewStyle = {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    // Critical page-zoom fix: stop the browser from hijacking touch gestures.
    // (react-native-web forwards unknown style keys to the DOM.)
    ...({ touchAction: 'none', cursor: isZoomed ? 'grab' : 'default' } as object),
    transform: [
      { translateX: zoom.translateX },
      { translateY: zoom.translateY },
      { scale: zoom.scale },
    ],
  };

  return (
    <Modal
      transparent
      visible={visible}
      onRequestClose={onClose}
      statusBarTranslucent
      animationType="none"
    >
      <Animated.View
        testID="viewer-backdrop"
        entering={reducedMotion ? undefined : FadeIn.duration(200)}
        exiting={reducedMotion ? undefined : FadeOut.duration(150)}
        style={{ flex: 1, backgroundColor: CHROME.backdrop }}
      >
        {/* Header: save/share (left), counter (center), close (right) */}
        <View
          className="flex-row items-center justify-between px-4"
          style={{ paddingTop: 16, zIndex: 2 }}
        >
          {/* Equal flex-1 sides keep the counter centered whatever the side widths. */}
          <View className="flex-1 flex-row" style={{ gap: 8 }}>
            <ChromeButton
              icon="download-outline"
              label="Save photo"
              onPress={() => void savePhotoWeb(photo)}
            />
            {showShare ? (
              <ChromeButton
                icon="share-outline"
                label="Share photo"
                onPress={() => void sharePhotoWeb(photo)}
              />
            ) : null}
          </View>
          {photos.length > 1 ? (
            <Text style={{ color: CHROME.ink, fontSize: 14, fontWeight: '600' }}>
              {`${safeIndex + 1} / ${photos.length}`}
            </Text>
          ) : (
            <View />
          )}
          <View className="flex-1 items-end">
            <ChromeButton icon="close" label="Close" onPress={onClose} />
          </View>
        </View>

        {/* Image + zoom/pan surface */}
        <View
          ref={containerRef}
          onLayout={onContainerLayout}
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          {/* Backdrop press-to-close, only when not zoomed. */}
          {!isZoomed ? (
            <Pressable
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close photo viewer"
            />
          ) : null}

          {imageFailed ? (
            <PhotoErrorFallback />
          ) : (
            <View
              testID="image-zoom-layer"
              pointerEvents="box-none"
              style={zoomLayerStyle}
            >
              <Image
                testID="viewer-image"
                source={{ uri: sources.full }}
                placeholder={{ uri: sources.medium }}
                placeholderContentFit="contain"
                style={{ width: '92%', height: '82%' }}
                contentFit="contain"
                onError={() => setImageFailed(true)}
                accessibilityLabel={buildPhotoAccessibilityLabel(
                  photo.caption,
                  safeIndex,
                  photos.length,
                  photo.altText
                )}
              />
            </View>
          )}

          {canGoPrev ? (
            <ChromeButton
              icon="chevron-back"
              label="Previous photo"
              onPress={goPrev}
              style={{ position: 'absolute', left: 12 }}
            />
          ) : null}
          {canGoNext ? (
            <ChromeButton
              icon="chevron-forward"
              label="Next photo"
              onPress={goNext}
              style={{ position: 'absolute', right: 12 }}
            />
          ) : null}
        </View>

        {/* Caption panel: renders nothing when the photo has no caption. Lifted
            above the zoom bar, which is drawn over it (zIndex 2). */}
        <ImageViewerCaption
          caption={photo.caption}
          altText={photo.altText}
          bottomOffset={ZOOM_BAR_HEIGHT}
          onPress={captionPressFor(photo.caption)}
        />

        {/* Zoom controls */}
        <View
          className="flex-row items-center justify-center"
          style={{
            paddingBottom: ZOOM_BAR_PADDING_BOTTOM,
            paddingTop: ZOOM_BAR_PADDING_TOP,
            gap: 12,
            zIndex: 2,
          }}
        >
          <ChromeButton
            icon="remove"
            label="Zoom out"
            onPress={() => zoomByStep(-ZOOM_STEP)}
          />
          <ChromeButton
            icon="add"
            label="Zoom in"
            onPress={() => zoomByStep(ZOOM_STEP)}
          />
        </View>
      </Animated.View>
    </Modal>
  );
}
